<template>
  <div class="survey-energy-slider">
    <div>
      <p class="survey-energy-slider__title">{{ title }}</p>
      <p v-if="hint" class="survey-energy-slider__hint">{{ hint }}</p>
    </div>

    <div class="survey-energy-slider__box">
      <div class="survey-energy-slider__field">
        <label class="survey-energy-slider__field-label">{{ fieldLabel }}</label>

        <a-input-number
            :value="modelValue"
            :min="energyMin"
            :max="energyMax"
            :controls="false"
            class="survey-energy-slider__input"
            @update:value="onInputChange"
        />
      </div>

      <div class="survey-energy-slider__slider-wrap">
        <div class="survey-energy-slider__track">
          <div class="survey-energy-slider__track-bg" />
          <div
              class="survey-energy-slider__track-fill"
              :style="{ '--slider-progress': `${clampedValue}%` }"
          />
          <input
              type="range"
              class="survey-energy-slider__range"
              :class="{ 'survey-energy-slider__range--active': isActive }"
              :style="{ '--slider-thumb-border': thumbBorderColor }"
              :min="energyMin"
              :max="energyMax"
              :value="modelValue"
              @input="onRangeInput"
              @mousedown="isActive = true"
              @mouseup="isActive = false"
              @mouseleave="isActive = false"
              @touchstart="isActive = true"
              @touchend="isActive = false"
          />
        </div>

        <div class="survey-energy-slider__labels">
          <span>Очень низкий</span>
          <span>Низкий</span>
          <span>Средний</span>
          <span>Высокий</span>
          <span>Очень высокий</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

const ENERGY_MIN = 0;
const ENERGY_MAX = 100;

const clamp = (value, min, max) => {
  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return min;
  }

  return Math.min(max, Math.max(min, Math.round(numeric)));
};

export default {
  name: 'SurveyEnergySliderQuestion',
  props: {
    title: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: '',
    },
    fieldLabel: {
      type: String,
      default: 'Уровень энергии',
    },
    modelValue: {
      type: Number,
      default: 50,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const isActive = ref(false);

    const clampedValue = computed(() => {
      const range = ENERGY_MAX - ENERGY_MIN;

      if (range <= 0) {
        return 0;
      }

      return Math.round(((clamp(props.modelValue, ENERGY_MIN, ENERGY_MAX) - ENERGY_MIN) / range) * 100);
    });

    const thumbBorderColor = computed(() => {
      if (clampedValue.value >= 75) {
        return '#62b84f';
      }

      return '#d9d9d9';
    });

    const updateValue = (value) => {
      emit('update:modelValue', clamp(value, ENERGY_MIN, ENERGY_MAX));
    };

    const onRangeInput = (event) => {
      updateValue(event.target.value);
    };

    const onInputChange = (value) => {
      updateValue(value);
    };

    return {
      isActive,
      energyMin: ENERGY_MIN,
      energyMax: ENERGY_MAX,
      clampedValue,
      thumbBorderColor,
      onRangeInput,
      onInputChange,
    };
  },
};
</script>

<style scoped lang="scss">
$survey-answer-bg: #00000005;

.survey-energy-slider {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__title {
    margin: 0 0 8px;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  &__hint {
    margin: 0;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
  }

  &__box {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    border-radius: 8px;
    background: $survey-answer-bg;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__field-label {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.45);
  }

  &__input {
    width: 130px;

    :deep(.ant-input-number) {
      width: 130px;
      border-radius: 6px;
      border: 1px solid var(--neutral-border, #d9d9d9);
    }

    :deep(.ant-input-number-input) {
      height: 30px;
      padding: 10px 12px;
      text-align: left;
      font-size: 14px;
      line-height: 12px;
      font-weight: 400;
    }
  }

  &__slider-wrap {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__track {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__track-bg,
  &__track-fill {
    position: absolute;
    left: 0;
    top: 50%;
    height: 16px;
    transform: translateY(-50%);
    border-radius: 8px;
  }

  &__track-bg {
    width: 100%;
    background: #f0f0f0;
  }

  &__track-fill {
    width: 100%;
    background: linear-gradient(90deg, #f65757 0%, #ff963f 25%, #dedf17 50%, #8ed163 75%, #52af46 100%);
    clip-path: inset(0 calc(100% - var(--slider-progress)) 0 0);
    pointer-events: none;
    z-index: 1;
  }

  &__range {
    position: relative;
    z-index: 2;
    width: 100%;
    margin: 0;
    appearance: none;
    background: transparent;
    cursor: pointer;

    &::-webkit-slider-runnable-track {
      height: 16px;
      background: transparent;
    }

    &::-moz-range-track {
      height: 16px;
      background: transparent;
    }

    &::-webkit-slider-thumb {
      appearance: none;
      width: 24px;
      height: 24px;
      margin-top: -4px;
      border-radius: 50%;
      border: 2px solid var(--slider-thumb-border);
      background: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
      transition: border-color 0.2s;
    }

    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 2px solid var(--slider-thumb-border);
      background: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
      transition: border-color 0.2s;
    }

    &:hover::-webkit-slider-thumb,
    &--active::-webkit-slider-thumb {
      border-color: var(--main-green);
    }

    &:hover::-moz-range-thumb,
    &--active::-moz-range-thumb {
      border-color: var(--main-green);
    }
  }

  &__labels {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    font-size: 12px;
    line-height: 120%;
    font-weight: 400;
    color: var(--grey-text);
    text-align: center;

    span {
      min-width: 0;

      &:first-child {
        text-align: left;
      }

      &:last-child {
        text-align: right;
      }
    }
  }
}
</style>
